import React, { useState, useEffect, useCallback } from 'react';
import {
  FiMapPin, FiPhone, FiMail, FiClock, FiGlobe,
  FiYoutube, FiInstagram, FiFacebook, FiLinkedin, FiTwitter,
  FiMessageCircle, FiFileText, FiShield, FiTruck, FiSave,
  FiRefreshCw,
} from 'react-icons/fi';
import API from '../../utils/api';
import toast from 'react-hot-toast';

// ── Tiny helpers ───────────────────────────────────────────────────────────────
const Card = ({ title, icon, children, onSave, saving }) => (
  <div style={{
    background: '#fff',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    marginBottom: 28,
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '18px 24px',
      borderBottom: '1px solid var(--border)',
      background: 'var(--bg-soft)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: '1rem' }}>
        {icon} {title}
      </div>
      <button
        onClick={onSave}
        disabled={saving}
        className="btn btn-primary btn-sm"
        style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 90 }}
      >
        {saving ? <FiRefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <FiSave size={14} />}
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
    <div style={{ padding: '24px' }}>{children}</div>
  </div>
);

const Field = ({ label, children, hint }) => (
  <div className="form-group" style={{ marginBottom: 18 }}>
    <label style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 6, display: 'block' }}>{label}</label>
    {children}
    {hint && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{hint}</div>}
  </div>
);

const Input = (props) => (
  <input className="form-control" style={{ fontSize: '0.9rem' }} {...props} />
);

const Textarea = (props) => (
  <textarea className="form-control" style={{ fontSize: '0.9rem', resize: 'vertical' }} {...props} />
);

// ── Main component ─────────────────────────────────────────────────────────────
const AdminSiteSettings = () => {
  const [loading, setLoading]   = useState(true);
  const [contact, setContact]   = useState({});
  const [social,  setSocial]    = useState({});
  const [legal,   setLegal]     = useState({});
  const [saving,  setSaving]    = useState({ contact: false, social: false, legal: false });
  const [activeTab, setActiveTab] = useState('contact');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get('/site-settings');
      const s = r.data.settings || {};
      setContact(s.contact  || {});
      setSocial(s.social    || {});
      setLegal(s.legal      || {});
    } catch {
      toast.error('Failed to load site settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (key, data) => {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await API.put(`/site-settings/${key}`, data);
      toast.success(`${key.charAt(0).toUpperCase() + key.slice(1)} settings saved!`);
    } catch {
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  const tabs = [
    { key: 'contact', label: 'Contact Details', icon: <FiMapPin size={15}/> },
    { key: 'social',  label: 'Social Links',    icon: <FiGlobe size={15}/> },
    { key: 'legal',   label: 'Legal Policies',  icon: <FiFileText size={15}/> },
  ];

  if (loading) return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <div className="spinner" />
      <p style={{ color: 'var(--text-muted)', marginTop: 12 }}>Loading settings…</p>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: 4 }}>Site Settings</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          Manage contact details, social links, and legal policy pages shown across the site.
        </p>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 4, background: 'var(--bg-soft)',
        borderRadius: 'var(--radius)', padding: 4, marginBottom: 28,
        border: '1px solid var(--border)', width: 'fit-content',
      }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '8px 18px', borderRadius: 'var(--radius)',
              border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
              background: activeTab === t.key ? '#fff' : 'transparent',
              color: activeTab === t.key ? 'var(--primary)' : 'var(--text-muted)',
              boxShadow: activeTab === t.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ── Contact Tab ── */}
      {activeTab === 'contact' && (
        <Card
          title="Contact Details"
          icon={<FiMapPin size={18} color="var(--primary)" />}
          onSave={() => save('contact', contact)}
          saving={saving.contact}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
            <Field
              label="Primary Address (Footer + Contact page)"
              hint="Main retail location. Shown in footer and as first address on Contact page."
            >
              <Textarea
                value={contact.primaryAddress || ''}
                onChange={e => setContact(c => ({ ...c, primaryAddress: e.target.value }))}
                rows={3}
                placeholder="e.g. Angel Printers, L-8, Bisanpura, Sector 58, Noida…"
              />
            </Field>
            <Field
              label="Branch Address (Contact page only)"
              hint="B2B / branch location. Shown only on the Contact Us page as second address."
            >
              <Textarea
                value={contact.branchAddress || ''}
                onChange={e => setContact(c => ({ ...c, branchAddress: e.target.value }))}
                rows={3}
                placeholder="e.g. Angel Printers (B2B), D-125, Sector 10, Noida…"
              />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
            <Field label={<><FiPhone size={13}/> Phone</>}>
              <Input
                value={contact.phone || ''}
                onChange={e => setContact(c => ({ ...c, phone: e.target.value }))}
                placeholder="+91 8585949411"
              />
            </Field>
            <Field label={<><FiMail size={13}/> Email</>}>
              <Input
                type="email"
                value={contact.email || ''}
                onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
                placeholder="info@angelprinters.com"
              />
            </Field>
            <Field label={<><FiClock size={13}/> Business Hours</>}>
              <Input
                value={contact.hours || ''}
                onChange={e => setContact(c => ({ ...c, hours: e.target.value }))}
                placeholder="Mon – Sat: 10am – 7pm IST"
              />
            </Field>
            <Field label={<><FiGlobe size={13}/> Website</>}>
              <Input
                value={contact.website || ''}
                onChange={e => setContact(c => ({ ...c, website: e.target.value }))}
                placeholder="www.angelprinters.com"
              />
            </Field>
          </div>

          <div style={{
            marginTop: 8, padding: '12px 16px',
            background: 'rgba(59,130,246,0.06)', borderRadius: 'var(--radius)',
            border: '1px solid rgba(59,130,246,0.15)', fontSize: '0.8rem', color: '#2563eb',
          }}>
            💡 <strong>Primary Address</strong> is used in the footer and the first card on Contact Us. <strong>Branch Address</strong> appears only on Contact Us as a second location.
          </div>
        </Card>
      )}

      {/* ── Social Tab ── */}
      {activeTab === 'social' && (
        <Card
          title="Social Links"
          icon={<FiGlobe size={18} color="var(--primary)" />}
          onSave={() => save('social', social)}
          saving={saving.social}
        >
          <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: 20 }}>
            Leave a field blank to hide that platform's icon in the footer.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
            {[
              { key: 'youtube',   label: 'YouTube',   icon: <FiYoutube size={14}/>,     placeholder: 'https://www.youtube.com/@yourhandle' },
              { key: 'instagram', label: 'Instagram', icon: <FiInstagram size={14}/>,   placeholder: 'https://www.instagram.com/yourhandle/' },
              { key: 'facebook',  label: 'Facebook',  icon: <FiFacebook size={14}/>,    placeholder: 'https://www.facebook.com/yourpage/' },
              { key: 'linkedin',  label: 'LinkedIn',  icon: <FiLinkedin size={14}/>,    placeholder: 'https://www.linkedin.com/company/yourcompany/' },
              { key: 'twitter',   label: 'Twitter / X', icon: <FiTwitter size={14}/>,   placeholder: 'https://twitter.com/yourhandle' },
              { key: 'whatsapp',  label: 'WhatsApp',  icon: <FiMessageCircle size={14}/>, placeholder: 'https://wa.me/918585949411' },
            ].map(s => (
              <Field key={s.key} label={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{s.icon} {s.label}</span>}>
                <Input
                  value={social[s.key] || ''}
                  onChange={e => setSocial(sc => ({ ...sc, [s.key]: e.target.value }))}
                  placeholder={s.placeholder}
                />
              </Field>
            ))}
          </div>
        </Card>
      )}

      {/* ── Legal Tab ── */}
      {activeTab === 'legal' && (
        <Card
          title="Legal Policy Pages"
          icon={<FiShield size={18} color="var(--primary)" />}
          onSave={() => save('legal', legal)}
          saving={saving.legal}
        >
          <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: 20 }}>
            Edit the text shown on <strong>/privacy</strong>, <strong>/terms</strong>, and <strong>/shipping</strong> pages.
            HTML is supported. Leave content blank to use the default built-in text.
          </p>

          {[
            { key: 'privacyPolicy',   label: 'Privacy Policy',  icon: <FiShield size={14}/>,    path: '/privacy' },
            { key: 'termsOfService',  label: 'Terms of Service',icon: <FiFileText size={14}/>,  path: '/terms' },
            { key: 'shippingPolicy',  label: 'Shipping Policy', icon: <FiTruck size={14}/>,     path: '/shipping' },
          ].map(p => {
            const group = legal[p.key] || {};
            const update = (field, val) => setLegal(l => ({
              ...l,
              [p.key]: { ...l[p.key], [field]: val },
            }));
            return (
              <div key={p.key} style={{
                border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                padding: 20, marginBottom: 20,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 16 }}>
                  {p.icon} {p.label}
                  <a href={p.path} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: '0.75rem', color: 'var(--primary)', marginLeft: 4 }}>
                    Preview ↗
                  </a>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 16, alignItems: 'start' }}>
                  <Field label="Last Updated Date">
                    <Input
                      value={group.lastUpdated || ''}
                      onChange={e => update('lastUpdated', e.target.value)}
                      placeholder="e.g. February 13, 2026"
                    />
                  </Field>
                  <div /> {/* spacer */}
                </div>
                <Field label="Policy Content (HTML supported)" hint="Leave blank to use the default built-in content.">
                  <Textarea
                    value={group.content || ''}
                    onChange={e => update('content', e.target.value)}
                    rows={14}
                    placeholder="<h2>Section Title</h2><p>Your policy text here...</p>"
                    style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}
                  />
                </Field>
              </div>
            );
          })}
        </Card>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default AdminSiteSettings;